import { useKnowledgeBaseOperations } from '@/hooks/useKnowledgeBaseOperations';
import { useKnowledgeBaseDocumentOperations } from '@/hooks/useKnowledgeBaseDocumentOperations';
import { useKnowledgeBaseShareOperations } from '@/hooks/useKnowledgeBaseShareOperations';
import type { KnowledgeBaseDocument } from '@/types/supabase';

export function useDialogHandlers(
  activeKnowledgeBase: any,
  activeDocument: KnowledgeBaseDocument | null,
  shareEmail: string,
  shareAccessLevel: 'View' | 'Edit' | 'Manage',
  setShareEmail: (email: string) => void,
  setOpen: (open: boolean) => void,
  setEditOpen: (open: boolean) => void,
  setDeleteOpen: (open: boolean) => void,
  fetchKnowledgeBaseDetails: () => Promise<void>,
  fetchKnowledgeBases: () => Promise<void>
) {
  const {
    handleCreateKnowledgeBase,
    handleUpdateKnowledgeBase,
    handleDeleteKnowledgeBase,
  } = useKnowledgeBaseOperations(fetchKnowledgeBaseDetails, fetchKnowledgeBases);

  const {
    addDocument: handleCreateDocument,
    updateDocument: handleUpdateDocument,
    deleteDocument: handleDeleteDocument,
  } = useKnowledgeBaseDocumentOperations(activeKnowledgeBase?.id, fetchKnowledgeBaseDetails, fetchKnowledgeBases);

  const {
    handleShareKnowledgeBase,
    handleUpdateShare,
    handleRemoveShare,
  } = useKnowledgeBaseShareOperations(fetchKnowledgeBaseDetails);

  const onCreateKnowledgeBase = async (values: any) => {
    const created = await handleCreateKnowledgeBase(values);
    if (created) {
      setOpen(false);
    }
  };

  const onUpdateKnowledgeBase = async (values: any) => {
    if (!activeKnowledgeBase) return;
    const updated = await handleUpdateKnowledgeBase(activeKnowledgeBase.id, values);
    if (updated) {
      setEditOpen(false);
    }
  };

  const onDeleteKnowledgeBase = async () => {
    if (!activeKnowledgeBase) return;
    const deleted = await handleDeleteKnowledgeBase(activeKnowledgeBase.id);
    if (deleted) {
      setDeleteOpen(false);
    }
  };

  const onCreateDocument = async (values: any, fileType: string, size: number) => {
    if (!activeKnowledgeBase) return null;
    return handleCreateDocument(values.name, values.content, fileType, size);
  };

  const onUpdateDocument = async (values: any, fileType?: string, size?: number) => {
    if (!activeDocument) return null;

    const updates: Partial<KnowledgeBaseDocument> = {
      name: values.name,
      content: values.content,
      file_type: fileType,
      size,
    };

    return handleUpdateDocument(activeDocument.id, updates);
  };

  const onDeleteDocument = async () => {
    if (!activeDocument) return false;
    return handleDeleteDocument(activeDocument.id);
  };

  const onShareKnowledgeBase = async () => {
    if (!activeKnowledgeBase) return;

    const shared = await handleShareKnowledgeBase(activeKnowledgeBase.id, shareEmail, shareAccessLevel);
    if (shared) {
      setShareEmail('');
    }
  };

  return {
    onCreateKnowledgeBase,
    onUpdateKnowledgeBase,
    onDeleteKnowledgeBase,
    onCreateDocument,
    onUpdateDocument,
    onDeleteDocument,
    onShareKnowledgeBase,
    handleUpdateShare,
    handleRemoveShare,
  };
}
